// Home.js
import React, { useEffect, useMemo, useState } from 'react';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import notoSansTagalog from '../Assests/NotoSansTagalog-Regular.ttf';
import logo1 from '../Assests/baybayinstuff.png';
import image12 from '../Assests/14.jpg';
import image13 from '../Assests/12.jpg';
import image14 from '../Assests/13.jpg';
import image15 from '../Assests/15.jpg';
import bg from '../Assests/bg.jpg';

import LoginModal from './Login';
import Translate from './Translate';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'NotoSansTagalog';
    src: url(${notoSansTagalog}) format('truetype');
    font-weight: normal;
    font-style: normal;
  }

  :root{
    --brand-maroon: #8B1D1D;   /* logo base */
    --brand-maroon-2: #6F1414; /* deeper */
    --brand-gold: #F6C945;     /* logo yellow */
    --brand-cream: #FFF6EA;    /* warm white */
    --ink: #201515;
  }

  * { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
`;

const glass = css`
  backdrop-filter: blur(28px) saturate(175%) brightness(1.03);
  -webkit-backdrop-filter: blur(28px) saturate(175%) brightness(1.03);

  border: 1px solid transparent;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04)) padding-box,
    linear-gradient(
      135deg,
      rgba(246,201,69,0.55),   /* gold edge */
      rgba(255,255,255,0.22),
      rgba(139,29,29,0.18)     /* maroon tint */
    ) border-box;

  box-shadow:
    0 18px 55px rgba(0,0,0,0.12),
    inset 0 1px 0 rgba(255,255,255,0.35);
`;

const glassSoft = css`
  backdrop-filter: blur(20px) saturate(165%) brightness(1.02);
  -webkit-backdrop-filter: blur(20px) saturate(165%) brightness(1.02);

  border: 1px solid transparent;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.03)) padding-box,
    linear-gradient(
      135deg,
      rgba(246,201,69,0.45),
      rgba(255,255,255,0.18),
      rgba(139,29,29,0.14)
    ) border-box;

  box-shadow:
    0 14px 45px rgba(0,0,0,0.10),
    inset 0 1px 0 rgba(255,255,255,0.28);
`;


const floatUp = keyframes`
  0% { transform: translateY(10px); opacity: 0; }
  100% { transform: translateY(0px); opacity: 1; }
`;

const shimmer = keyframes`
  0% { transform: translateX(-120%) rotate(12deg); opacity: 0; }
  30% { opacity: 1; }
  100% { transform: translateX(120%) rotate(12deg); opacity: 0; }
`;

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScanOpen, setIsScanOpen] = useState(false);

  // Baybayin animation states
  const [isBaybayin, setIsBaybayin] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [isForward, setIsForward] = useState(true);

  const toggleModal = () => setIsModalOpen((v) => !v);
  const toggleScan = () => setIsScanOpen((v) => !v);

  const englishText = 'Reviving Tradition, Revolutionized By Innovation';
  const baybayinText =
    'ᜉᜄ᜔ᜊᜓᜑᜌ᜔ ᜐ ᜆ᜔ᜍᜇᜒᜐ᜔ᜌᜓᜈ᜔, ᜉᜒᜈᜁᜄ᜔ᜆᜒᜅ᜔ ᜅ᜔ ᜁᜈᜓᜊᜐ᜔ᜌᜓᜈ᜔';

  const currentText = isBaybayin ? baybayinText : englishText;
  const letters = useMemo(() => currentText.split(''), [currentText]);

  // A) Toggle text (English <-> Baybayin) every 5s
  useEffect(() => {
    const textInterval = setInterval(() => {
      setIsBaybayin((prev) => !prev);
      setHighlightIndex(0);
      setIsForward(true);
    }, 5000);

    return () => clearInterval(textInterval);
  }, []);

  // B) Animate highlight back-and-forth over current letters
  useEffect(() => {
    if (!letters.length) return;

    const letterInterval = setInterval(() => {
      setHighlightIndex((prev) => {
        if (isForward) {
          if (prev < letters.length - 1) return prev + 1;
          setIsForward(false);
          return Math.max(prev - 1, 0);
        } else {
          if (prev > 0) return prev - 1;
          setIsForward(true);
          return Math.min(prev + 1, letters.length - 1);
        }
      });
    }, 85);

    return () => clearInterval(letterInterval);
  }, [letters.length, isForward]);

  return (
    <>
      <GlobalStyle />

      <HeroContainer>
        {/* LEFT */}
        <LeftSection>
          <LeftGlassCard>
            
            <RotatorShell aria-hidden="true">
              <RotatingBox>
                {[image12, image13, image14, image15].map((img, index) => (
                  <BoxSpan key={index} style={{ '--i': index + 1 }}>
                    <BoxImage src={img} alt={`image-${index + 12}`} />
                  </BoxSpan>
                ))}
              </RotatingBox>
            </RotatorShell>

            <HeroSubtitle $isBaybayin={isBaybayin}>
              {letters.map((letter, index) => (
                <LetterSpan
                  key={`${letter}-${index}-${isBaybayin}`}
                  $isHighlighted={index === highlightIndex}
                  $isBaybayin={isBaybayin}
                >
                  {letter}
                </LetterSpan>
              ))}
            </HeroSubtitle>

            <SubCopy>
              Recognize Baybayin characters instantly, or open the scanner for real-time capture and translation.
            </SubCopy>

            <ButtonRow>
              <GlassButton onClick={toggleModal}>Recognize</GlassButton>
              <GlassButton onClick={toggleScan} $variant="primary">
                eBaybayScan
              </GlassButton>
            </ButtonRow>

            <FootHint>Tip: Use Recognize to login, then access full features after authentication.</FootHint>
          </LeftGlassCard>
        </LeftSection>

        {/* RIGHT */}
        <RightSection>
          <RightGlassWrap>
        

            {/* Poster: responsive + not too tall so footer stays visible */}
            <PosterFrame>
              <PosterImage src={logo1} alt="Baybayin Merchandise" />
            </PosterFrame>

            <RightFooter>
              <LogoText></LogoText>
              <MiniRow>
                <MiniChip>Merch</MiniChip>
                <MiniChip>Baybayin</MiniChip>
                <MiniChip>Culture</MiniChip>
              </MiniRow>
            </RightFooter>
          </RightGlassWrap>
        </RightSection>

        <LoginModal isOpen={isModalOpen} toggleModal={toggleModal} />
        {isScanOpen && <Translate isFloating={true} onClose={toggleScan} />}
      </HeroContainer>
    </>
  );
};

export default Home;

/* =========================
   Layout + Background
   ========================= */
const HeroContainer = styled.section`
  display: flex;
  min-height: 100vh;
  padding-top: 96px;
  position: relative;
  overflow: hidden;
  width: 100%;

  /* ✅ Direct background photo (NO white opacity layer) */
  background: url(${bg}) center / cover no-repeat;

  /* optional: make photo darker a bit so glass is clearer */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    /* IMPORTANT: this is NOT white wash; it's just subtle darkening */
    background: rgba(0,0,0,0.18);
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 980px) {
    flex-direction: column;
    padding-top: 90px;
  }
`;


const LeftSection = styled.div`
  flex: 1;
  position: relative;
  display: grid;
  place-items: center;
  padding: 2.2rem 1.25rem;
  overflow: hidden;

  background:
    radial-gradient(900px 700px at 20% 40%, rgba(255,255,255,0.12), transparent 60%),
    linear-gradient(135deg, var(--brand-maroon-2) 0%, #A33822 55%, #C86A2A 105%);

  border-top-right-radius: 60%;
  border-bottom-right-radius: 60%;

  &::before{
    content:'';
    position:absolute;
    inset:-40%;
    background:
      radial-gradient(closest-side at 35% 35%, rgba(246,201,69,0.20), transparent 62%),
      radial-gradient(closest-side at 70% 80%, rgba(255,255,255,0.12), transparent 60%);
    transform: rotate(-10deg);
    pointer-events:none;
  }

  &::after{
    content:'';
    position:absolute;
    width:60%;
    height:140%;
    top:-20%;
    left:-10%;
    background: linear-gradient(120deg,
      rgba(255,255,255,0.00),
      rgba(246,201,69,0.14),
      rgba(255,255,255,0.00)
    );
    transform: rotate(12deg);
    filter: blur(2px);
    opacity: 0.35;
    pointer-events:none;
  }

  @media (max-width: 980px) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 55%;
    border-bottom-right-radius: 55%;
  }
`;
const RightSection = styled.div`
  flex: 1;
  position: relative;
  display: grid;
  place-items: center;
  padding: 2.4rem 1.25rem;

  background: transparent;
`;


/* =========================
   Glass Cards
   ========================= */

const LeftGlassCard = styled.div`
  ${glass}
  width: min(640px, 92%);
  border-radius: 28px;
  padding: 1.55rem 1.55rem 1.35rem;
  position: relative;
  z-index: 2;
  animation: ${floatUp} 420ms ease both;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 28px;
    background:
      radial-gradient(800px 280px at 20% 0%, rgba(255,255,255,0.32), transparent 60%),
      radial-gradient(900px 320px at 90% 10%, rgba(255,255,255,0.18), transparent 60%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -20%;
    left: -40%;
    width: 70%;
    height: 160%;
    background: linear-gradient(
      120deg,
      rgba(255,255,255,0.00),
      rgba(255,255,255,0.16),
      rgba(255,255,255,0.00)
    );
    transform: rotate(14deg);
    animation: ${shimmer} 6s ease-in-out infinite;
    pointer-events: none;
    opacity: 0.55;
  }

  @media (max-width: 520px) {
    padding: 1.25rem 1.15rem 1.05rem;
    border-radius: 24px;
    &::before { border-radius: 24px; }
  }
`;

const RightGlassWrap = styled.div`
  ${glass}
  width: min(720px, 92%);
  border-radius: 28px;
  padding: 1.8rem 1.8rem 1.4rem;
  position: relative;
  animation: ${floatUp} 520ms ease both;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  /* Keeps footer visible on smaller screens */
  max-height: calc(100vh - 110px);
  overflow: auto;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 28px;
    background:
      radial-gradient(900px 300px at 20% 0%, rgba(255,255,255,0.32), transparent 60%),
      radial-gradient(900px 320px at 90% 10%, rgba(255,255,255,0.18), transparent 60%);
    pointer-events: none;
  }

  @media (max-width: 520px) {
    padding: 1.35rem 1.15rem 1.05rem;
    border-radius: 24px;
    &::before { border-radius: 24px; }
  }
`;

/* =========================
   Left Content
   ========================= */

const RotatorShell = styled.div`
  ${glassSoft}
  width: clamp(230px, 30vw, 290px);
  height: clamp(230px, 30vw, 290px);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.12);
  display: grid;
  place-items: center;
  overflow: hidden;
  position: relative;
  z-index: 2;
  margin: 0.2rem 0 0.85rem;

  &::before {
    content: '';
    position: absolute;
    inset: -20%;
    background: radial-gradient(closest-side at 50% 45%, rgba(255,255,255,0.18), transparent 65%);
    pointer-events: none;
  }
`;

const HeroSubtitle = styled.h2`
  position: relative;
  z-index: 2;
  margin: 0.2rem 0 0.35rem;

  font-size: ${(p) => (p.$isBaybayin ? '2.05rem' : '1.85rem')};
  font-family: ${(p) => (p.$isBaybayin ? 'NotoSansTagalog, sans-serif' : 'Poppins, sans-serif')};
  opacity: ${(p) => (p.$isBaybayin ? 0.95 : 1)};
  line-height: 1.15;

  color: rgba(255, 255, 255, 0.98);
  text-shadow: 0 14px 34px rgba(0, 0, 0, 0.30);

  @media (max-width: 520px) {
    font-size: ${(p) => (p.isBaybayin ? '1.8rem' : '1.6rem')};
  }
`;

const LetterSpan = styled.span`
  color: ${(p) => (p.$isHighlighted ? 'var(--brand-gold)' : 'rgba(255,255,255,0.96)')};
  transition: color 0.22s ease, text-shadow 0.22s ease;
  font-family: ${(p) => (p.$isBaybayin ? 'NotoSansTagalog, sans-serif' : 'Poppins, sans-serif')};

  ${(p) =>
    p.$isHighlighted &&
    css`
      text-shadow: 0 0 18px rgba(246, 201, 69, 0.55);
    `}
`;

const SubCopy = styled.p`
  position: relative;
  z-index: 2;
  margin: 0.15rem 0 1rem;
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.98rem;
  line-height: 1.55;
  max-width: 520px;
`;

const ButtonRow = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  gap: 0.9rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 0.25rem;
`;

const GlassButton = styled.button`
  ${glassSoft}
  border-radius: 999px;
  cursor: pointer;

  color: var(--brand-maroon);
  font-weight: 900;
  padding: 0.85rem 1.55rem;

  border: 1px solid transparent;
  background:
    linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04)) padding-box,
    linear-gradient(135deg, rgba(246,201,69,0.55), rgba(139,29,29,0.20)) border-box;

  ${(p) =>
    p.$variant === 'primary' &&
    css`
      color: var(--ink);
      background:
        linear-gradient(135deg, rgba(246,201,69,0.55), rgba(246,201,69,0.22)) padding-box,
        linear-gradient(135deg, rgba(246,201,69,0.70), rgba(139,29,29,0.22)) border-box;
    `}
`;


const FootHint = styled.div`
  position: relative;
  z-index: 2;
  margin-top: 0.9rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.82);
`;

/* =========================
   Right Content (Poster)
   ========================= */

const PosterFrame = styled.div`
  ${glassSoft}
  position: relative;
  z-index: 2;
  border-radius: 22px;
  overflow: hidden;

  width: min(520px, 92%);
  max-height: clamp(300px, 42vh, 420px);

  display: grid;
  place-items: center;

  background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.06));
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 26px 80px rgba(0, 0, 0, 0.18);
`;

const PosterImage = styled.img`
  width: 100%;
  height: auto;
  max-height: clamp(300px, 42vh, 420px);
  object-fit: contain;
  display: block;
`;

const RightFooter = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  place-items: center;
  margin-top: 1.05rem;
`;

const LogoText = styled.h3`
  color: var(--brand-maroon);
  font-weight: 1000;
`;


const MiniRow = styled.div`
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const MiniChip = styled.div`
  ${glassSoft}
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.58);
  color: rgba(25, 25, 25, 0.80);
  font-weight: 900;
  font-size: 0.88rem;
`;

/* =========================
   Rotating 3D Box (Tight)
   ========================= */

const RotatingBox = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  transform-style: preserve-3d;
  animation: animate 18s linear infinite;

  @media (max-width: 520px) {
    width: 170px;
    height: 170px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @keyframes animate {
    0% { transform: perspective(1000px) rotateY(0deg); }
    100% { transform: perspective(1000px) rotateY(360deg); }
  }
`;

const BoxSpan = styled.span`
  position: absolute;
  inset: 0;
  transform-origin: center;
  transform-style: preserve-3d;

  /* tightened so it doesn't look too big */
  transform: rotateY(calc(var(--i) * 90deg)) translateZ(210px);

  -webkit-box-reflect: below 0px linear-gradient(transparent, transparent, #0003);

  @media (max-width: 520px) {
    transform: rotateY(calc(var(--i) * 90deg)) translateZ(180px);
  }
`;

const BoxImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.24);
`;
